import Link from 'next/link'
import LanguageSwitcher from '../components/LanguageSwitcher'
import SEO from '../components/SEO'
import { LanguageProvider, useLanguage } from '../context/LanguageContext'
import ABOUT_COPY from '../locales/about'


export async function getServerSideProps(context) {
  const INTERNAL_API = process.env.INTERNAL_API_URL || 'http://backend:8000'
  let suggestedLanguage = 'en'
  try {
    const cfCountry = context.req.headers['cf-ipcountry'] || ''
    const res = await fetch(`${INTERNAL_API}/v1/geo/`, {
      headers: { 'CF-IPCountry': cfCountry },
    })
    if (res.ok) {
      const data = await res.json()
      if (['en', 'ht', 'fr', 'es', 'pt-BR', 'zh-Hans'].includes(data.suggested_language)) {
        suggestedLanguage = data.suggested_language
      }
    }
  } catch (_) { /* geo hint is best-effort */ }
  return { props: { suggestedLanguage } }
}


export default function AboutPage({ suggestedLanguage }) {
  return (
    <LanguageProvider initialSuggestion={suggestedLanguage}>
      <About />
    </LanguageProvider>
  )
}


function About() {
  const { language } = useLanguage()
  const t = ABOUT_COPY[language] || ABOUT_COPY.en

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <SEO
        title={t.seo_title}
        description={t.seo_description}
        path="/about"
        lang={language}
      />
      <LanguageSwitcher />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t.h1}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Mission Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.mission_h2}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {t.mission_p1}
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            {t.mission_p2}
          </p>
        </div>

        {/* Technology Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.tech_h2}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {t.tech_p1}
          </p>
          <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
            {t.tech_bullets.map((b, i) => (<li key={i}>{b}</li>))}
          </ul>
        </div>

        {/* Coming Soon Section */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-8 mb-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-purple-900 dark:text-purple-100 mb-2">
                {t.voices_h2}
              </h2>
              <p className="text-purple-800 dark:text-purple-200 mb-3">
                {t.voices_p1}
              </p>
              <div className="space-y-2 text-purple-700 dark:text-purple-300">
                {t.voices_bullets.map((b, i) => (
                  <p key={i} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{b}</span>
                  </p>
                ))}
              </div>
              <p className="mt-4 text-sm text-purple-600 dark:text-purple-400 font-medium">
                {t.voices_waitlist}
              </p>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.commit_h2}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {t.commit_p1}
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            {t.commit_p2}
          </p>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.cta_h3}
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/text-to-speech" className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
              {t.cta_try}
            </Link>
            <Link href="/contact" className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-semibold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
              {t.cta_contact}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
